import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, Award, BookOpen, Users } from "lucide-react";

const credentials = [
  { label: "MBBS", detail: "Burdwan Medical College" },
  { label: "DNB Obstetrics & Gynaecology (NBEMS)", detail: "National Board of Examinations in Medical Sciences" },
  { label: "DrNB Gynaecologic Oncology", detail: "Tata Medical Center" },
  { label: "Certified Robotic Surgeon", detail: "Intuitive Surgicals" },
];

const memberships = [
  "Association Of Gynecologic Oncologists of India (AGOI)",
  "Association Of Gynecological Robotic Surgeons of India (AGRS)",
  "The European Society Of Gynecologic Oncology (ESGO)",
  "International Gynecologic Cancer Society (IGCS)",
  "Asian Society of Gynaecologic Oncology (ASGO)",
];

const stats = [
  { value: "10+", label: "Years of Experience" },
  { value: "3500+", label: "Cases Treated" },
];

export default function AboutSection() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 bg-[#F9F9F6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Label */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4"
        >
          About the Doctor
        </motion.p>

        {/* Top: Name + Bio */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
          >
            <h2
              data-testid="about-heading"
              className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1E3F33] leading-tight mb-2"
            >
              Dr. Neha Agarwal
            </h2>
            <p className="text-base sm:text-lg font-medium text-[#8FA998] mb-6">
              MBBS, MS (OBG), DNB (Gynecologic Oncology)
            </p>
            <p
              data-testid="about-description"
              className="text-base sm:text-lg text-[#4A6359] leading-relaxed mb-5"
            >
              Dr. Neha Agarwal is a highly experienced gynecologic oncologist in Kolkata, specializing in the diagnosis and treatment of cancers affecting women, including ovarian, cervical, uterine, vulval, and vaginal cancers.
            </p>
            <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed mb-8">
              With extensive training in advanced cancer surgery, including HIPEC (Hyperthermic Intraperitoneal Chemotherapy) and minimally invasive procedures.</p>
            <a
              href="/about"
              data-testid="about-learn-more-btn"
              className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors duration-200 group"
            >
              Learn More About Doctor
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>

          {/* Quote */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="flex items-center"
          >
            <blockquote
              data-testid="about-quote"
              className="font-serif text-3xl sm:text-4xl italic text-[#1E3F33] leading-snug border-l-4 border-[#8FA998] pl-8"
            >
              "Ensuring both medical excellence and emotional support throughout the cancer journey."
            </blockquote>
          </motion.div>
        </div>

        {/* Stats Bar */}
        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 gap-4 mb-16"
          data-testid="about-stats"
        >
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              data-testid={`about-stat-${i}`}
              className="bg-[#F2EFE9] rounded-2xl p-6 text-center"
            >
              <p className="font-serif text-4xl sm:text-5xl font-bold text-[#1E3F33] mb-1">
                {stat.value}
              </p>

              <p className="text-sm sm:text-base text-[#4A6359] font-medium">
                {stat.label}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Credentials + Memberships */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Qualifications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8"
            data-testid="about-credentials"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#1E3F33]/10 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-[#1E3F33]" />
              </div>
              <h3 className="font-serif text-2xl text-[#1E3F33]">
                Qualifications & Training
              </h3>
            </div>
            <div className="space-y-4">
              {credentials.map((cred, i) => (
                <div
                  key={i}
                  data-testid={`credential-${i}`}
                  className="flex items-start gap-3 pb-4 border-b border-[#D8DDD9] last:border-0 last:pb-0"
                >
                  <div className="w-2 h-2 rounded-full bg-[#8FA998] mt-2 flex-shrink-0" />
                  <div>
                    <p className="text-base font-semibold text-[#1E3F33]">{cred.label}</p>
                    <p className="text-sm text-[#4A6359]">{cred.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Memberships */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8"
            data-testid="about-memberships"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#1E3F33]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#1E3F33]" />
              </div>
              <h3 className="font-serif text-2xl text-[#1E3F33]">
                Professional Memberships
              </h3>
            </div>
            <div className="space-y-4">
              {memberships.map((m, i) => (
                <div
                  key={i}
                  data-testid={`membership-${i}`}
                  className="flex items-center gap-3 pb-4 border-b border-[#D8DDD9] last:border-0 last:pb-0"
                >
                  <Award className="w-4 h-4 text-[#C29F60] flex-shrink-0" />
                  <p className="text-base text-[#1E3F33] font-medium">{m}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
